import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import {
  SparklesIcon,
  RocketLaunchIcon,
  TrophyIcon,
  ShieldCheckIcon,
  BoltIcon,
  HeartIcon,
  ChartBarIcon,
  StarIcon,
  ArrowRightIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';

import PlanCard from '../components/subscription/PlanCard';
import ComparisonTable from '../components/subscription/ComparisonTable';
import subscriptionAPI from '../services/subscriptionAPI';
import paymentService from '../services/paymentService';

const SubscriptionPlans = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, lab } = useSelector((state) => state.auth);
  const comparisonRef = useRef(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  // Redirect if not logged in or not a lab
  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    if (user.role !== 'lab') {
      navigate('/');
      return;
    }
    
    // Redirect premium labs to subscription management
    const isLabPremium = lab?.isPremium && lab?.premiumUntil && new Date(lab.premiumUntil) > new Date();
    if (isLabPremium) {
      navigate('/lab/subscription');
      return;
    }
  }, [user, lab, navigate]);

  // Handle subscription selection
  const handleSubscription = async () => {
    if (isProcessing) return;
    
    setIsProcessing(true);
    
    try {
      // Initiate subscription payment
      const response = await subscriptionAPI.initiateSubscription();
      const { subscription, payment } = response;
      
      // Show payment modal
      setShowPaymentModal(true);
      
      // Initialize Moyasar payment
      setTimeout(async () => {
        const container = document.querySelector('.mysr-form-subscription');
        if (container) container.innerHTML = '';

        await paymentService.initializeMoyasar('.mysr-form-subscription', {
          publishable_api_key: payment.publishable_api_key,
          amount: payment.amount,
          currency: payment.currency,
          description: payment.description,
          callback_url: `${window.location.origin}/subscription-success/${subscription._id}`,
          methods: ['creditcard'],
          metadata: payment.metadata || {},
          on_completed: async (paymentResult) => {
            console.log('Subscription payment completed:', paymentResult);
            setTimeout(() => {
              window.location.href = `${window.location.origin}/subscription-success/${subscription._id}?payment_id=${paymentResult.id}`;
            }, 1000);
          },
          on_failure: (error) => {
            console.error('Subscription payment failed:', error);
            toast.error('فشلت عملية الدفع. يرجى المحاولة مرة أخرى.');
            setIsProcessing(false);
            setShowPaymentModal(false);
          }
        });
      }, 0);
      
    } catch (error) {
      console.error('Subscription initiation error:', error);
      toast.error(error || 'فشل في بدء عملية الاشتراك');
      setIsProcessing(false);
    }
  };

  const plans = [
    {
      type: 'free',
      name: t('subscription.freePlan'),
      description: t('subscription.freeDescription'),
      price: 0,
      features: [
        t('subscription.features18Commission'),
        t('subscription.featuresBasicSupport'),
        t('subscription.basicReports')
      ]
    },
    {
      type: 'badeel_plus',
      name: t('subscription.badeelPlus'),
      description: t('subscription.premiumDescription'),
      price: 2760,
      originalPrice: 2400,
      features: [
        t('subscription.featuresFullExemption'),
        t('subscription.features10FreeExchanges'),
        t('subscription.featuresPremiumSupportPriority')
      ]
    }
  ];

  const handleSelectPlan = (plan) => {
    if (plan.type === 'badeel_plus') {
      handleSubscription();
    }
  };

  const benefits = [
    {
      icon: SparklesIcon,
      title: t('subscription.guaranteedSavings'),
      description: t('subscription.savingsDescription'),
      color: 'from-brand-blue to-brand-green'
    },
    {
      icon: BoltIcon,
      title: t('subscription.freeExchangeTitle'),
      description: t('subscription.freeExchangeDescription'),
      color: 'from-brand-green to-brand-blue'
    },
    {
      icon: TrophyIcon,
      title: t('subscription.premiumSupportTitle'),
      description: t('subscription.premiumSupportDescription'),
      color: 'from-brand-blue to-brand-green'
    },
    {
      icon: ShieldCheckIcon,
      title: t('subscription.exclusiveFeaturesTitle'),
      description: t('subscription.exclusiveFeaturesDescription'),
      color: 'from-brand-green to-brand-blue'
    }
  ];



  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.6 }
    }
  };

  if (!user || user.role !== 'lab') {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-primary-50 to-secondary-50">
      {/* Floating Background Elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <motion.div
          className="absolute -top-24 -right-24 w-96 h-96 bg-gradient-to-br from-brand-blue to-brand-green rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob"
          animate={{
            x: [0, 100, 0],
            y: [0, -100, 0],
            scale: [1, 1.2, 1],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            repeatType: "reverse",
          }}
        />
        <motion.div
          className="absolute -bottom-24 -left-24 w-96 h-96 bg-gradient-to-br from-brand-green to-brand-blue rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob"
          animate={{
            x: [0, -100, 0],
            y: [0, 100, 0],
            scale: [1, 0.8, 1],
          }}
          transition={{
            duration: 25,
            repeat: Infinity,
            repeatType: "reverse",
          }}
        />
      </div>

      <div className="relative z-10">
        {/* Hero Section */}
        <motion.section
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="pt-24 pb-16"
        >
          <div className="container mx-auto px-4 text-center">
            <motion.div
              variants={itemVariants}
              className="mb-8"
            >
              <motion.div
                animate={{
                  rotate: [0, 10, -10, 0],
                  scale: [1, 1.1, 1],
                }}
                transition={{
                  duration: 4,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
                className="inline-block mb-6"
              >
                <div className="w-24 h-24 bg-gradient-to-br from-brand-blue to-brand-green rounded-3xl flex items-center justify-center shadow-2xl">
                  <TrophyIcon className="w-12 h-12 text-white" />
                </div>
              </motion.div>

              <h1 className="text-5xl md:text-7xl font-black mb-8 bg-gradient-to-r from-brand-blue via-brand-blueSecondary to-brand-green bg-clip-text text-transparent leading-relaxed" style={{ lineHeight: '1.2' }}>
                {t('subscription.badeelPlus')}
              </h1>
              
              <p className="text-2xl md:text-3xl text-gray-700 mb-4 font-light">
                {t('subscription.premiumPackage')}
              </p>
              
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                {t('subscription.description')}
              </p>
            </motion.div>

            {/* Quick Stats */}
            <motion.div
              variants={itemVariants}
              className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto mb-12"
            >
              {[
                { number: '0%', label: t('subscription.platformCommission'), icon: SparklesIcon },
                { number: '10', label: t('subscription.freeExchange'), icon: BoltIcon },
                { number: '24/7', label: t('subscription.premiumSupport'), icon: ShieldCheckIcon },
                { number: '2,760', label: t('subscription.yearlyPrice'), icon: TrophyIcon }
              ].map((stat, index) => (
                <motion.div
                  key={index}
                  variants={itemVariants}
                  className="bg-white/80 backdrop-blur-lg rounded-2xl p-6 shadow-lg border border-white/50"
                >
                  <stat.icon className="w-8 h-8 text-brand-blue mx-auto mb-3" />
                  <div className="text-2xl font-black text-brand-blue">{stat.number}</div>
                  <div className="text-sm text-gray-600">{stat.label}</div>
                </motion.div>
              ))}
            </motion.div>

            {/* CTA Buttons */}
            <motion.div
              variants={itemVariants}
              className="flex flex-col sm:flex-row gap-4 justify-center items-center"
            >
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => handleSelectPlan(plans[1])}
                className="px-8 py-4 bg-brand-blue text-white rounded-2xl font-bold text-lg shadow-xl hover:shadow-2xl transition-all duration-300"
              >
                <span className="flex items-center gap-3">
                  <RocketLaunchIcon className="w-6 h-6" />
                  {t('subscription.subscribeNow')}
                  <ArrowRightIcon className="w-5 h-5 group-hover:-translate-x-2 transition-transform" />
                </span>
              </motion.button>

              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => comparisonRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                className="px-8 py-4 bg-white/80 backdrop-blur-lg border-2 border-brand-blue text-brand-blue rounded-2xl font-bold text-lg hover:bg-brand-blue hover:text-white transition-all duration-300"
              >
                <span className="flex items-center gap-3">
                  <ChartBarIcon className="w-6 h-6" />
                  {t('subscription.comparePlans')}
                </span>
              </motion.button>
            </motion.div>
          </div>
        </motion.section>

        {/* Plans Section */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-4xl md:text-5xl font-black mb-4 text-gray-800">
                {t('subscription.chooseRightPlan')}
              </h2>
              <p className="text-xl text-gray-600">
                {t('subscription.flexiblePackages')}
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              {plans.map((plan, index) => (
                <PlanCard
                  key={plan.type}
                  plan={plan}
                  isPopular={plan.type === 'badeel_plus'}
                  onSelectPlan={handleSelectPlan}
                />
              ))}
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-16 bg-white/50">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-4xl md:text-5xl font-black mb-6 bg-gradient-to-r from-brand-blue to-brand-green bg-clip-text text-transparent leading-relaxed" style={{ lineHeight: '1.3' }}>
                {t('subscription.whyBadeelPlus')}
              </h2>
              <p className="text-xl text-gray-600">
                {t('subscription.exclusiveFeatures')}
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {benefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ y: -10, scale: 1.02 }}
                  className="bg-white/80 backdrop-blur-lg rounded-3xl p-8 shadow-xl border border-white/50 text-center group"
                >
                  <div className={`w-16 h-16 bg-gradient-to-r ${benefit.color} rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform`}>
                    <benefit.icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-800 mb-4">{benefit.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Comparison Table */}
        <motion.section
          ref={comparisonRef}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="py-16"
        >
          <div className="container mx-auto px-4">
            {/* Comparison content */}
            <ComparisonTable onSelectPlan={handleSelectPlan} />
          </div>
        </motion.section>



        {/* Final CTA */}
        <section className="py-16">
          <div className="container mx-auto px-4 text-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="bg-gradient-to-r from-brand-blue to-brand-green rounded-3xl p-12 text-white relative overflow-hidden"
            >
              {/* Background Pattern */}
              <div className="absolute inset-0 opacity-10">
                {[...Array(20)].map((_, i) => (
                  <motion.div
                    key={i}
                    className="absolute w-2 h-2 bg-white rounded-full"
                    style={{
                      left: `${Math.random() * 100}%`,
                      top: `${Math.random() * 100}%`,
                    }}
                    animate={{
                      y: [0, -100, 0],
                      opacity: [0, 1, 0],
                    }}
                    transition={{
                      duration: 3 + Math.random() * 2,
                      repeat: Infinity,
                      delay: Math.random() * 2,
                    }}
                  />
                ))}
              </div>

              <div className="relative z-10">
                <RocketLaunchIcon className="w-16 h-16 mx-auto mb-6 animate-bounce" />
                <h2 className="text-4xl md:text-6xl font-black mb-6">
                  {t('subscription.startYourJourney')}
                </h2>
                <p className="text-xl md:text-2xl mb-8 opacity-90">
                  {t('subscription.joinHundreds')}
                </p>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => handleSelectPlan(plans[1])}
                  className="px-12 py-6 bg-white text-brand-blue rounded-2xl font-black text-xl shadow-2xl hover:shadow-3xl transition-all duration-300"
                >
                  <span className="flex items-center gap-3">
                    <SparklesIcon className="w-6 h-6" />
                    {t('subscription.subscribeNowPrice')}
                    <ArrowRightIcon className="w-6 h-6" />
                  </span>
                </motion.button>
                <p className="text-sm mt-4 opacity-75">
                  {t('subscription.moneyBackGuarantee')}
                </p>
              </div>
            </motion.div>
          </div>
        </section>
      </div>

      {/* Payment Modal */}
      {showPaymentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl p-8 max-w-md w-full mx-4"
          >
            <div className="text-center mb-6">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">
                اشتراك بديل بلس
              </h3>
              <p className="text-gray-600">
                أدخل بيانات البطاقة الائتمانية لإتمام الاشتراك
              </p>
            </div>
            
            <div className="mysr-form-subscription mb-6"></div>
            
            <button
              onClick={() => {
                setShowPaymentModal(false);
                setIsProcessing(false);
              }}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              إلغاء
            </button>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionPlans;
